import { LocalPortal } from "components/ui/LocalPortal";
import { usePropagationContext } from "context/propagationContext";
import { useStopPropagation } from "hooks/useStopPropagation";
import { type IUseTooltipOptions, useTooltip } from "hooks/useTooltip";
import { useEffect, useMemo } from "react";
import * as React from "react";

import { cn } from "@/lib/utils/cn";

import { Typography } from "../Typography";

export type TTooltipProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: React.ReactElement<any & { ref?: React.Ref<any> }, any>; // The element to which the tooltip is attached
  clean?: boolean; // If true, the tooltip will rendered with no styles
  content: FC | React.ReactNode; // The tooltip content
  escapeOverlay?: boolean; // If true, render in the document body instead of a parent tooltip/overlay — use for tooltips nested inside a scrollable/clipped tooltip
  notInteractive?: boolean; // If true, the tooltip will not be interactive
  tooltipRef?: React.MutableRefObject<HTMLElement | null>; // The ref of the tooltip itself
  triggerRef?: React.Ref<HTMLElement | null>; // The ref of the element to which the tooltip is attached
} & Omit<IUseTooltipOptions, "interactive">;

const Tooltip: FC<TTooltipProps> = ({
  children,
  className,
  clean = false,
  content: Content,
  escapeOverlay = false,
  notInteractive = false,
  tooltipRef: propTooltipRef,
  triggerRef: propTriggerRef,
  ...tooltipOptions
}) => {
  const { setTooltipRef, setTriggerRef, tooltipProps, tooltipRef, triggerRef, visible } = useTooltip({
    ...tooltipOptions,
    interactive: !notInteractive,
  });

  const {
    actions: { addPropagationElement, removePropagationElement },
  } = usePropagationContext();

  useEffect(() => {
    addPropagationElement(tooltipRef);
    return () => removePropagationElement(tooltipRef);
  }, [tooltipRef, addPropagationElement, removePropagationElement]);

  const stopPropagation = useStopPropagation();

  const contentDiv = useMemo(
    () =>
      Content
        ? (
            typeof Content === "string"
              ? (
                  <Typography variant="body_reg">{Content}</Typography>
                )
              : typeof Content === "function"
                ? (
                    <Content />
                  )
                : (
                    Content
                  )
          )
        : null,
    [Content],
  );

  const childrenToRender = useMemo(
    () => React.cloneElement(children, { ...children.props, ref: setTriggerRef }),
    [children, setTriggerRef],
  );

  useEffect(() => {
    if (propTriggerRef && Content) {
      if (typeof propTriggerRef === "function") {
        propTriggerRef(triggerRef);
      } else {
        // eslint-disable-next-line react-hooks/immutability -- Forwarding ref value to external ref object
        (propTriggerRef as React.MutableRefObject<HTMLElement | null>).current = triggerRef;
      }
    }
  }, [propTriggerRef, triggerRef, Content]);

  useEffect(() => {
    if (propTooltipRef !== undefined) {
      propTooltipRef.current = tooltipRef;
    }
  }, [propTooltipRef, tooltipRef]);

  if (!Content) return children;

  return (
    <React.Fragment>
      {childrenToRender}

      {visible && (
        <LocalPortal escapeOverlay={escapeOverlay} parentContainer={tooltipRef}>
          <div
            ref={setTooltipRef}
            {...tooltipProps}
            className={cn(
              "z-1000",
              !clean && "flex items-center justify-center rounded-sm border border-border bg-popover px-3 py-2 text-popover-foreground shadow-xl min-w-5 min-h-5 max-w-100",
              className,
            )}
            onClick={stopPropagation}
            onMouseDown={stopPropagation}
          >
            {contentDiv}
          </div>
        </LocalPortal>
      )}
    </React.Fragment>
  );
};

const MemoizedTooltip = React.memo(Tooltip) as typeof Tooltip;
export { MemoizedTooltip as Tooltip };
